import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Pagination,
    TextField,
    Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { schoolApi } from '../../common/schoolApi';
import { QuestionView } from './QuestionView';

/**
 * Searching WiZi's two banks and adding questions to a test.
 *
 * The bank never sends an answer key -- a teacher sees the key once the
 * question is on their test, where it is the test's own copy. Each result
 * says whether it can be added (`usable` / `problem`) so a question with a
 * broken key is greyed out here instead of refused on submit.
 *
 * Adding is partial on the API side: what can be added is, and the rest come
 * back with a reason each. Those reasons are shown, not swallowed.
 */

type Tax = { id: number; name: string; question_count: number };

const LEVELS = ['Elementary', 'Moderate', 'Advance'];
const PAGE = 20;

export const BankPanel = ({
    testId,
    language,
    subjectId,
    onAdded,
}: {
    testId: number;
    language: string;
    subjectId: number | null;
    onAdded: () => void;
}) => {
    const notify = useNotify();

    const [subjects, setSubjects] = useState<Tax[]>([]);
    const [chapters, setChapters] = useState<Tax[]>([]);
    const [topics, setTopics] = useState<Tax[]>([]);

    const [subject, setSubject] = useState<number | ''>(subjectId ?? '');
    const [chapter, setChapter] = useState<number | ''>('');
    const [topic, setTopic] = useState<number | ''>('');
    const [level, setLevel] = useState('');
    const [bank, setBank] = useState('');
    const [answerType, setAnswerType] = useState('');
    const [pyq, setPyq] = useState(false);
    const [page, setPage] = useState(1);

    const [items, setItems] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [picked, setPicked] = useState<Record<string, { source: string; id: number }>>({});
    const [marks, setMarks] = useState('4');
    const [negative, setNegative] = useState('1');
    const [rejected, setRejected] = useState<any[]>([]);

    // The taxonomy cascade: subject -> chapters -> topics, each with counts.
    useEffect(() => {
        schoolApi.get('school-test/taxonomy').then(r => setSubjects(r.result.items)).catch(e => notify(e.message, { type: 'error' }));
    }, []);
    useEffect(() => {
        setChapters([]);
        setChapter('');
        if (subject === '') return;
        schoolApi.get('school-test/taxonomy', { subject_id: subject }).then(r => setChapters(r.result.items)).catch(() => {});
    }, [subject]);
    useEffect(() => {
        setTopics([]);
        setTopic('');
        if (chapter === '') return;
        schoolApi.get('school-test/taxonomy', { chapter_id: chapter }).then(r => setTopics(r.result.items)).catch(() => {});
    }, [chapter]);

    const search = async (p = page) => {
        setLoading(true);
        try {
            const r = await schoolApi.get('school-test/bank', {
                subject_id: subject,
                chapter_id: chapter,
                topic_id: topic,
                level,
                bank,
                answer_type: answerType,
                pyq: pyq ? 1 : '',
                language, // a question in another language would be refused anyway
                test_id: testId,
                page: p,
                pagesize: PAGE,
            });
            setItems(r.result);
            setTotal(r.total || 0);
        } catch (e: any) {
            notify(e.message, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
        search(1);
    }, [subject, chapter, topic, level, bank, answerType, pyq]);

    const toggle = (q: any) => {
        const k = `${q.source}:${q.id}`;
        setPicked(prev => {
            const next = { ...prev };
            if (next[k]) delete next[k];
            else next[k] = { source: q.source, id: q.id };
            return next;
        });
    };

    const add = async () => {
        const questions = Object.values(picked);
        if (questions.length === 0) return;
        try {
            const r = await schoolApi.post(`school-test/add_questions/${testId}`, {
                questions,
                marks: Number(marks),
                negative_marks: Number(negative),
            });
            const added = r.result.added.length;
            setRejected(r.result.rejected);
            setPicked({});
            notify(`${added} question${added === 1 ? '' : 's'} added${r.result.rejected.length ? `, ${r.result.rejected.length} not added` : ''}.`, {
                type: r.result.rejected.length ? 'warning' : 'success',
            });
            onAdded();
            search(page);
        } catch (e: any) {
            notify(e.message, { type: 'error' });
        }
    };

    const count = Object.keys(picked).length;
    const sel = (label: string, value: any, set: (v: any) => void, options: { id: any; name: string }[], width = 170) => (
        <TextField select size="small" label={label} value={value} onChange={e => set(e.target.value)} sx={{ minWidth: width }}>
            <MenuItem value="">
                <em>Any</em>
            </MenuItem>
            {options.map(o => (
                <MenuItem key={o.id} value={o.id}>
                    {o.name}
                </MenuItem>
            ))}
        </TextField>
    );
    const withCount = (t: Tax[]) => t.map(x => ({ id: x.id, name: `${x.name} (${x.question_count})` }));

    return (
        <Card variant="outlined" sx={{ width: '100%', mb: 3 }}>
            <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                    Add questions from the WiZi bank
                </Typography>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    {sel('Subject', subject, setSubject, withCount(subjects), 200)}
                    {sel('Chapter', chapter, setChapter, withCount(chapters), 220)}
                    {sel('Topic', topic, setTopic, withCount(topics), 200)}
                    {sel('Level', level, setLevel, LEVELS.map(l => ({ id: l, name: l })), 140)}
                    {sel('Bank', bank, setBank, [
                        { id: 'wizi_question', name: 'Mock bank' },
                        { id: 'question', name: 'Practice bank' },
                    ])}
                    {sel('Answer', answerType, setAnswerType, [
                        { id: 'mcq', name: 'Multiple choice' },
                        { id: 'integer', name: 'Integer' },
                    ], 150)}
                    <FormControlLabel control={<Checkbox checked={pyq} onChange={e => setPyq(e.target.checked)} />} label="Past-year only" />
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                        {loading ? 'Searching…' : `${total.toLocaleString()} question${total === 1 ? '' : 's'} found`}
                    </Typography>
                    <TextField size="small" label="Marks each" type="number" value={marks} onChange={e => setMarks(e.target.value)} sx={{ width: 110 }} />
                    <TextField
                        size="small"
                        label="Minus for wrong"
                        type="number"
                        value={negative}
                        onChange={e => setNegative(e.target.value)}
                        sx={{ width: 140 }}
                        helperText="0 = no negative"
                    />
                    <Button variant="contained" disabled={count === 0} onClick={add}>
                        Add {count || ''} selected
                    </Button>
                </Box>

                {rejected.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRejected([])}>
                        Not added:
                        <ul style={{ margin: 0 }}>
                            {rejected.map(x => (
                                <li key={`${x.source}:${x.id}`}>
                                    {x.source === 'wizi_question' ? 'Mock' : 'Practice'} #{x.id} &mdash; {x.reason}
                                </li>
                            ))}
                        </ul>
                    </Alert>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 1.5 }}>
                    {items.map(q => {
                        const k = `${q.source}:${q.id}`;
                        const disabled = !q.usable || q.in_test;
                        return (
                            <Box
                                key={k}
                                onClick={() => !disabled && toggle(q)}
                                sx={{
                                    border: 1,
                                    borderColor: picked[k] ? 'primary.main' : 'divider',
                                    borderRadius: 1,
                                    p: 1,
                                    cursor: disabled ? 'default' : 'pointer',
                                    opacity: disabled ? 0.55 : 1,
                                    bgcolor: picked[k] ? 'action.selected' : 'transparent',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Checkbox size="small" checked={!!picked[k]} disabled={disabled} sx={{ p: 0.5 }} />
                                    <Typography variant="caption" color={q.in_test ? 'success.main' : q.usable ? 'text.secondary' : 'error'}>
                                        {q.in_test ? 'Already on this test' : q.usable ? `#${q.id}` : q.problem}
                                    </Typography>
                                </Box>
                                <QuestionView q={q} compact />
                            </Box>
                        );
                    })}
                </Box>

                {total > PAGE && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                            count={Math.ceil(total / PAGE)}
                            page={page}
                            onChange={(_, p) => {
                                setPage(p);
                                search(p);
                            }}
                        />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};
